/* eslint-disable jsx-a11y/iframe-has-title */
import React from "react";
import { connect } from "react-redux";

import AppState from "../../foundation/AppState";
import MessagingContact from "../../types/core/MessagingContact";
import { IAppState } from "../../state/Store";
import { AuthJWTPayload } from "../../types/system/AuthJWT";
import { MessageDetailMessage } from "../../types/system/MessageDetail";
import { setMessagingOpen } from "../../state/system/UIConfiguration";
import Utility from "../../foundation/Utility";
import Logger from "../../foundation/Logger";
import {
    deleteMessage,
    setThreadMessageLink,
} from "../../state/system/Messaging";

interface MessagingMessagesProps {
    userId: number;
    jwt: AuthJWTPayload;
    isSystem: boolean;
    broadcasting: boolean;
    body: string;
    contacts: Array<MessagingContact>;
    recipients: Array<MessagingContact>;
    thread: Array<MessageDetailMessage>;
    deleteMessage: (message: MessageDetailMessage) => void;
    setMessagingOpen: (value: boolean) => void;
    setThreadMessageLink: (messageId: string, rect: DOMRect) => void;
}

interface MessagingMessagesState {
    frames: Array<{
        target: React.RefObject<HTMLIFrameElement>;
        message: MessageDetailMessage;
    }>;
}

class MessagingMessages extends React.Component<
    MessagingMessagesProps,
    MessagingMessagesState
> {
    private linkRegex = /<a class="btn" href="(.*)">/;

    state = {
        frames: [],
    };

    disableText(): boolean {
        return !(this.props.recipients?.length > 0) && !this.props.broadcasting;
    }

    isImageEdit(): boolean {
        return (
            !this.disableText() &&
            !!this.props.body &&
            this.props.body.indexOf('<p><img src="data:image/jpeg;base64') >= 0
        );
    }

    isBroadcast(message: MessageDetailMessage) {
        return message.UserID === 1;
    }

    isImage(message: MessageDetailMessage) {
        return (
            message.Message.indexOf('<p><img src="data:image/jpeg;base64') >= 0
        );
    }

    isEmoji(message: MessageDetailMessage) {
        return (
            (message.Emojis?.length ?? 0) <= 10 &&
            !(message.Emojiless?.length ?? 0)
        );
    }

    isLinkBtn(message: MessageDetailMessage): boolean {
        return this.linkRegex.test(message.Message);
    }

    includeUser(index: number) {
        if (this.props.recipients.length <= 1) return false;

        let previous = index > 0 ? this.props.thread[index - 1] : null;
        let current = this.props.thread[index];

        return (
            (current.UserID ?? 0) > 0 &&
            (!previous || previous.UserID !== current.UserID)
        );
    }

    getSender(userId: number) {
        var sender = this.props.recipients.find(item => item.UserID === userId);

        return sender ? sender.Display : "";
    }

    launchLink(message: MessageDetailMessage) {
        if (!this.isLinkBtn(message)) return;
        const match = this.linkRegex.exec(message.Message);
        if (match?.[1]) {
            const url = new URL(match?.[1]).clean();
            const crt = document.location;
            if (url.sameBase(crt)) {
                document.location.pathname = url.removeBase();
            } else {
                document.location = `./SafeUrlCheck?url=${encodeURIComponent(
                    String(url),
                )}`;
            }
        }
        this.props.setMessagingOpen(false);
    }

    includeDate(index: number) {
        let previous = index > 0 ? this.props.thread[index - 1] : null;
        let current = this.props.thread[index];
        let next =
            index < this.props.thread.length - 1
                ? this.props.thread[index + 1]
                : null;

        let currentGap =
            Math.abs(new Date().secondsBetween(new Date(current.Sent ?? 0))) >
            300;

        let nextGap, previousGap: boolean;

        if (!previous) previousGap = currentGap;
        else
            previousGap = this.checkDates(
                previous.Sent.toDate(),
                current.Sent.toDate(),
            );

        if (!next) nextGap = currentGap;
        else
            nextGap = this.checkDates(
                next.Sent.toDate(),
                current.Sent.toDate(),
            );

        return (previousGap && nextGap) || nextGap;
    }

    checkDates(date1: Date | string, date2: Date | string) {
        return Math.abs(new Date(date1).secondsBetween(new Date(date2))) > 300;
    }

    async resize(target: HTMLIFrameElement, message: MessageDetailMessage) {
        if (!!target.parentElement) {
            target.parentElement.parentElement.style.visibility = "hidden";
            target.parentElement.style.width = "100%";
        }
        target.style.width = "100%";
        target.style.height = "";

        await Utility.sleep();

        try {
            target.style.height =
                (target.contentWindow?.document.body.scrollHeight ?? 0) + "px";
        } catch (error: any) {
            Logger.error(error);
        }

        try {
            const btn = target.contentWindow?.document.querySelector(".btn");
            if (!!btn) {
                const rect = btn.getBoundingClientRect();
                this.props.setThreadMessageLink(
                    message.Id,
                    new DOMRect(
                        rect.left + 10,
                        rect.top + 5,
                        rect.width,
                        rect.height,
                    ),
                );
            }
        } catch (error: any) {
            Logger.error(error);
        }

        await Utility.sleep();

        try {
            target.style.height =
                (target.contentWindow?.document.body.scrollHeight ?? 0) +
                2 +
                "px";
            target.style.width =
                Math.min(
                    (target.contentWindow?.document.body.scrollWidth ?? 0) + 22,
                    target.offsetWidth,
                ) + "px";
            if (!!target.parentElement) {
                target.parentElement.style.width = "";
                target.parentElement.parentElement.style.visibility = "visible";
            }
        } catch (error: any) {
            Logger.error(error);
        }
    }

    registerFrame(
        event: React.SyntheticEvent<HTMLIFrameElement, Event>,
        message: MessageDetailMessage,
    ) {
        const target = event.target as HTMLIFrameElement;
        this.state.frames.push({ target: target, message: message });
        this.resize(target, message);
    }

    messageKeyPress(
        event: React.KeyboardEvent,
        message: MessageDetailMessage,
    ): void {
        if (event.key === "Delete" || event.key === "Backspace") {
            this.props.deleteMessage(message);
        }
    }

    render(): React.ReactNode {
        return (
            !this.props.broadcasting && (
                <div
                    className={`col message-thread${
                        this.props.isSystem ? " broadcasts" : ""
                    }`}>
                    {(this.props.isSystem ||
                        this.props.recipients.length > 0) &&
                        this.props.thread.map((message, index) => (
                            <span
                                id={`message${message.Id}`}
                                key={index}
                                className={`${
                                    message.UserID === this.props.userId
                                        ? "mine"
                                        : ""
                                }${
                                    this.isBroadcast(message)
                                        ? " broadcast"
                                        : ""
                                }${this.isImage(message) ? " image" : ""}${
                                    this.isEmoji(message) ? " emoji" : ""
                                }`}
                                onKeyUp={event =>
                                    this.messageKeyPress(event, message)
                                }
                                tabIndex={0}>
                                <span
                                    className={`${
                                        this.includeUser(index) ? "" : "hidden"
                                    }`}>
                                    {this.getSender(message.UserID)}
                                </span>
                                <span>
                                    <iframe
                                        onLoad={event =>
                                            this.registerFrame(event, message)
                                        }
                                        srcDoc={message.Message}
                                        tabIndex={-1}
                                        sandbox="allow-same-origin"></iframe>
                                    <span>
                                        {this.isLinkBtn(message) && (
                                            <span
                                                className="has-link"
                                                style={{
                                                    top: message.LinkTop,
                                                    left: message.LinkLeft,
                                                    width: message.LinkWidth,
                                                    height: message.LinkHeight,
                                                }}
                                                onClick={() =>
                                                    this.launchLink(message)
                                                }></span>
                                        )}
                                    </span>
                                </span>
                                <span></span>
                                {this.includeDate(index) && (
                                    <span>
                                        {message.Sent.toDate().toLocaleString()}
                                    </span>
                                )}
                            </span>
                        ))}
                </div>
            )
        );
    }
}

const mapStateToProps = (state: IAppState) => {
    const { messaging } = state.db;
    return {
        userId: Number(AppState.jwt.sub),
        contacts: messaging.contacts,
        recipients: messaging.recipients,
        jwt: AppState.jwt,
        thread: messaging.thread,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        setMessagingOpen: (value: boolean) => dispatch(setMessagingOpen(value)),
        setThreadMessageLink: (messageId: string, rect: DOMRect) =>
            dispatch(
                setThreadMessageLink({
                    id: messageId,
                    rect: rect,
                }),
            ),
        deleteMessage: (message: MessageDetailMessage) =>
            dispatch(deleteMessage(message)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(MessagingMessages);
